import { getStoredToken } from './authService';
import { API_BASE_URL } from '../config/api';
import { generateImageForLesson } from './imageService';

// ========================================
// Types for French Service
// ========================================

export interface FrenchWord {
  word: string;
  english: string;
  dictionaryForm: string;
  usedForm: string;
  wordType: string; // noun, verb, adjective, adverb, etc.
}

export interface GrammarPoint {
  title: string;
  explanation: string;
  examples: string[];
}

export interface FrenchLesson {
  lesson_id?: string;
  lesson_name: string;
  lesson_objective: string;
  category: string;
  title: string;
  text: string;
  words: FrenchWord[];
  grammarPoints: GrammarPoint[];
  level: string; // A1, A2, B1, B2
  image_url?: string;
  image_prompt?: string;
  created_at?: string;
  updated_at?: string;
}

export interface LessonSummary {
  lesson_id: string;
  lesson_name: string;
  lesson_objective: string;
  category: string;
  created_at: string;
  updated_at: string;
}

// Helper function to get auth token
const getAuthToken = async (): Promise<string> => {
  const token = getStoredToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return token;
};

// Helper function to make authenticated API calls
const apiCall = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
};

// ========================================
// French Service Class
// ========================================

export class FrenchService {
  /**
   * Build system prompt for text generation only
   */
  private static buildTextPrompt(pastLessons?: LessonSummary[], customInstruction?: string): string {
    let prompt = `You are a French language teacher creating reading passages for learners at various levels (A1-B2).

Your task is to generate a French reading passage.

REQUIREMENTS:
- Create a passage of 400-500 words in French
- Mix difficulty levels (A1-B2) - the lesson can include elements from different levels
- Topics: daily life, French culture, stories, travel, food, family, work, hobbies, etc.
- Include important grammatical structures relevant to the content
- Make the content engaging, practical, and authentic
- The passage should tell a complete story or describe a situation`;

    if (pastLessons && pastLessons.length > 0) {
      const historyContext = pastLessons
        .map(l => `- "${l.lesson_name}": ${l.lesson_objective}`)
        .join('\n');
      
      prompt += `\n\nPREVIOUS LESSONS COVERED:\n${historyContext}\n\n`;
      prompt += `Generate a NEW lesson that builds upon or complements these topics. `;
      prompt += `Avoid repeating the same content from previous lessons.\n`;
    }

    prompt += `\n\nRESPONSE FORMAT:
You must respond with valid JSON in this exact format:
{
  "lesson_name": "Short lesson title in English (e.g., 'Shopping at the Market')",
  "lesson_objective": "Brief description of what this lesson teaches (e.g., 'Learn vocabulary for shopping and basic conversational phrases')",
  "level": "A1" or "A2" or "B1" or "B2",
  "text": "Complete French text passage (400-500 words)"
}

IMPORTANT:
- Focus on creating natural, flowing French text
- Use varied vocabulary and sentence structures
- Make sure the JSON is valid and properly formatted
- The text should be interesting and educational
- Ensure the response is complete and not truncated`;

    return prompt;
  }

  /**
   * Build system prompt for vocabulary and grammar extraction
   */
  private static buildVocabularyAndGrammarPrompt(text: string): string {
    return `You are a French language teacher analyzing a text for learners at various levels (A1-B2).

Your task is to extract key vocabulary and identify grammar points from the provided French text.

FRENCH TEXT:
${text}

REQUIREMENTS FOR VOCABULARY:
- Extract important words and phrases that students should learn
- Include common words, useful phrases, and key vocabulary
- For each word/phrase provide:
  * word: The word as it appears in context (may be conjugated/modified)
  * english: Clear English translation
  * dictionaryForm: The base/infinitive form (e.g., "avoir" for "avait", "grand" for "grande")
  * usedForm: The form used in the text with explanation (e.g., "avait (imparfait)", "grande (feminine singular)")
  * wordType: Type of word (noun, verb, adjective, adverb, pronoun, preposition, conjunction, etc.)
- Include approximately 30-50 vocabulary items
- Focus on words useful for A1-B2 level learners

REQUIREMENTS FOR GRAMMAR:
- Identify 4-6 key grammar concepts present in the text
- For each grammar point provide:
  * title: Grammar concept name (e.g., "Passé Composé", "Partitive Articles")
  * explanation: Clear, concise explanation of the grammar rule
  * examples: 3-4 examples from the text or closely related examples

RESPONSE FORMAT:
You must respond with valid JSON in this exact format:
{
  "words": [
    {
      "word": "French word as used",
      "english": "English translation",
      "dictionaryForm": "base form",
      "usedForm": "form with context",
      "wordType": "part of speech"
    }
  ],
  "grammarPoints": [
    {
      "title": "Grammar concept name",
      "explanation": "Clear explanation",
      "examples": ["Example 1", "Example 2", "Example 3"]
    }
  ]
}

IMPORTANT:
- Order words as they appear in the text
- Make sure the JSON is valid and properly formatted
- Provide accurate translations and forms
- Grammar explanations should be clear and educational`;
  }

  /**
   * Generate French text only (Step 1)
   */
  private static async generateText(
    pastLessons?: LessonSummary[], 
    customInstruction?: string
  ): Promise<{ lesson_name: string; lesson_objective: string; level: string; text: string }> {
    try {
      console.log('🇫🇷 [FrenchService] Step 1: Generating French text...');

      const systemPrompt = this.buildTextPrompt(pastLessons, customInstruction);
      
      let userMessage = 'Generate a new French reading passage following the requirements.';
      if (customInstruction) {
        userMessage = `Generate a new French reading passage following the requirements. Focus the lesson on: ${customInstruction}`;
      }

      const messages = [
        {
          role: 'user' as const,
          content: userMessage
        }
      ];

      const aiRequest = {
        provider: 'claude',
        model: 'claude-haiku-4-5-20251001',
        systemPrompt: systemPrompt,
        messages: messages,
        maxTokens: 8000,
        temperature: 0.8
      };

      const result = await apiCall('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(aiRequest)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to generate French text');
      }

      // Check if response was truncated due to max_tokens
      if (result.stop_reason === 'max_tokens') {
        console.warn('⚠️ [FrenchService] AI text response was truncated due to max_tokens limit');
        throw new Error('AI response was incomplete. Please try again.');
      }

      // Extract the AI response
      let aiResponse: string = '';
      
      // Check result.data.content first (wrapped response)
      if (result.data?.content && Array.isArray(result.data.content) && result.data.content.length > 0) {
        const textContent = result.data.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.data.response
      if (!aiResponse && result.data?.response && typeof result.data.response === 'string') {
        aiResponse = result.data.response;
      }
      
      // Check result.content array
      if (!aiResponse && Array.isArray(result.content) && result.content.length > 0) {
        const textContent = result.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.response
      if (!aiResponse && typeof result.response === 'string' && result.response) {
        aiResponse = result.response;
      }
      
      // Check result.content as string
      if (!aiResponse && typeof result.content === 'string' && result.content) {
        aiResponse = result.content;
      }
      
      if (!aiResponse) {
        console.error('🇫🇷 [FrenchService] Failed to extract response. Full result:', JSON.stringify(result, null, 2));
        throw new Error('AI returned empty response for text generation');
      }

      console.log('🇫🇷 [FrenchService] Raw AI response length:', aiResponse.length);
      console.log('🇫🇷 [FrenchService] First 200 chars:', aiResponse.substring(0, 200));

      // Parse JSON from the response
      let jsonString = aiResponse.trim();
      
      // Remove markdown code blocks if present
      const jsonMatch = jsonString.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        jsonString = jsonMatch[1].trim();
      }
      
      console.log('🇫🇷 [FrenchService] After markdown removal, length:', jsonString.length);
      console.log('🇫🇷 [FrenchService] First 300 chars:', jsonString.substring(0, 300));
      
      // Check if JSON appears to be truncated (incomplete)
      if (!jsonString.endsWith('}') && !jsonString.endsWith(']')) {
        console.error('🇫🇷 [FrenchService] JSON appears to be truncated');
        throw new Error('AI response was incomplete (truncated JSON). Please try again.');
      }
      
      let textData;
      try {
        textData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error('🇫🇷 [FrenchService] JSON parse error for text:', parseError);
        console.error('🇫🇷 [FrenchService] Character at error position:');
        if (parseError instanceof SyntaxError) {
          const match = parseError.message.match(/position (\d+)/);
          if (match) {
            const pos = parseInt(match[1]);
            console.error('Context around position', pos, ':', jsonString.substring(Math.max(0, pos - 50), Math.min(jsonString.length, pos + 50)));
          }
        }
        
        // If it's a truncation error, provide better user feedback
        if (parseError instanceof Error && 
            (parseError.message.includes('Unexpected end of JSON') ||
             parseError.message.includes('Unexpected token'))) {
          throw new Error('AI response was incomplete. Please try again.');
        }
        
        throw new Error(`Failed to parse text JSON: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
      }
      
      if (!textData || typeof textData !== 'object') {
        throw new Error('Invalid JSON response - expected an object');
      }
      
      if (!textData.lesson_name || !textData.lesson_objective || !textData.level || !textData.text) {
        throw new Error('Invalid text structure - missing lesson_name, lesson_objective, level, or text');
      }
      
      if (typeof textData.lesson_name !== 'string' || 
          typeof textData.lesson_objective !== 'string' || 
          typeof textData.level !== 'string' ||
          typeof textData.text !== 'string') {
        throw new Error('Invalid text structure - all fields must be strings');
      }

      console.log('🇫🇷 [FrenchService] Text generated successfully:', {
        lesson_name: textData.lesson_name,
        lesson_objective: textData.lesson_objective,
        level: textData.level,
        textLength: textData.text.length,
        textPreview: textData.text.substring(0, 100) + '...'
      });

      return {
        lesson_name: textData.lesson_name,
        lesson_objective: textData.lesson_objective,
        level: textData.level,
        text: textData.text
      };
    } catch (error) {
      console.error('🇫🇷 [FrenchService] Error generating text:', error);
      throw error;
    }
  }

  /**
   * Generate vocabulary and grammar from French text (Step 2)
   */
  private static async generateVocabularyAndGrammar(text: string): Promise<{ words: FrenchWord[]; grammarPoints: GrammarPoint[] }> {
    try {
      console.log('🇫🇷 [FrenchService] Step 2: Generating vocabulary and grammar...');

      const systemPrompt = this.buildVocabularyAndGrammarPrompt(text);
      
      const messages = [
        {
          role: 'user' as const,
          content: 'Extract and analyze the key vocabulary and grammar from this text following the requirements.'
        }
      ];

      const aiRequest = {
        provider: 'claude',
        model: 'claude-haiku-4-5-20251001',
        systemPrompt: systemPrompt,
        messages: messages,
        maxTokens: 16000, // Increased for extensive vocabulary and grammar extraction
        temperature: 0.3 // Lower temperature for more accurate analysis
      };

      const result = await apiCall('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(aiRequest)
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to generate vocabulary and grammar');
      }

      // Check if response was truncated due to max_tokens
      if (result.stop_reason === 'max_tokens') {
        console.warn('⚠️ [FrenchService] AI vocabulary/grammar response was truncated due to max_tokens limit');
        throw new Error('AI response was incomplete. Please try again.');
      }

      // Extract the AI response
      let aiResponse: string = '';
      
      // Check result.data.content first (wrapped response)
      if (result.data?.content && Array.isArray(result.data.content) && result.data.content.length > 0) {
        const textContent = result.data.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.data.response
      if (!aiResponse && result.data?.response && typeof result.data.response === 'string') {
        aiResponse = result.data.response;
      }
      
      // Check result.response
      if (!aiResponse && typeof result.response === 'string' && result.response) {
        aiResponse = result.response;
      }
      
      // Check result.content array
      if (!aiResponse && Array.isArray(result.content) && result.content.length > 0) {
        const textContent = result.content.find((c: any) => c.type === 'text');
        aiResponse = textContent?.text || '';
      }
      
      // Check result.content as string
      if (!aiResponse && typeof result.content === 'string' && result.content) {
        aiResponse = result.content;
      }
      
      if (!aiResponse) {
        console.error('🇫🇷 [FrenchService] Failed to extract vocabulary response. Full result:', JSON.stringify(result, null, 2));
        throw new Error('AI returned empty response for vocabulary and grammar generation');
      }

      // Parse JSON from the response
      let jsonString = aiResponse.trim();
      
      // Remove markdown code blocks if present
      const jsonMatch = jsonString.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        jsonString = jsonMatch[1].trim();
      }
        
        // Check if JSON appears to be truncated (incomplete)
        if (!jsonString.endsWith('}') && !jsonString.endsWith(']')) {
          console.error('🇫🇷 [FrenchService] JSON appears to be truncated');
        throw new Error('AI response was incomplete (truncated JSON). Please try again.');
      }
      
      let vocabGrammarData;
      try {
        vocabGrammarData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error('🇫🇷 [FrenchService] JSON parse error for vocabulary/grammar:', parseError);
        console.error('🇫🇷 [FrenchService] Raw AI response:', aiResponse.substring(0, 200));
        console.error('🇫🇷 [FrenchService] Attempted to parse:', jsonString.substring(0, 500));
        
        // If it's a truncation error, provide better user feedback
        if (parseError instanceof Error && 
            (parseError.message.includes('Unexpected end of JSON') ||
             parseError.message.includes('Unexpected token'))) {
          throw new Error('AI response was incomplete. Please try again.');
        }
        
        throw new Error(`Failed to parse vocabulary/grammar JSON: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
      }
      
      if (!vocabGrammarData || typeof vocabGrammarData !== 'object') {
        throw new Error('Invalid vocabulary/grammar JSON response - expected an object');
      }
      
      if (!Array.isArray(vocabGrammarData.words)) {
        throw new Error('Invalid vocabulary structure - words must be an array');
      }
      
      if (!Array.isArray(vocabGrammarData.grammarPoints)) {
        throw new Error('Invalid grammar structure - grammarPoints must be an array');
      }
      
      // Validate each word has required fields
      const validWords = vocabGrammarData.words.filter((word: any) => {
        return word && 
               typeof word.word === 'string' && 
               typeof word.english === 'string' && 
               typeof word.dictionaryForm === 'string' && 
               typeof word.usedForm === 'string' && 
               typeof word.wordType === 'string';
      });
      
      // Validate each grammar point has required fields
      const validGrammarPoints = vocabGrammarData.grammarPoints.filter((point: any) => {
        return point && 
               typeof point.title === 'string' && 
               typeof point.explanation === 'string' && 
               Array.isArray(point.examples);
      });
      
      if (validWords.length === 0) {
        throw new Error('No valid vocabulary words found');
      }
      
      if (validGrammarPoints.length === 0) {
        throw new Error('No valid grammar points found');
      }

      console.log('🇫🇷 [FrenchService] Vocabulary and grammar generated successfully:', {
        totalWords: vocabGrammarData.words.length,
        validWords: validWords.length,
        totalGrammarPoints: vocabGrammarData.grammarPoints.length,
        validGrammarPoints: validGrammarPoints.length
      });

      return {
        words: validWords,
        grammarPoints: validGrammarPoints
      };
    } catch (error) {
      console.error('🇫🇷 [FrenchService] Error generating vocabulary and grammar:', error);
      throw error;
    }
  }

  /**
   * Generate a new French lesson using AI and save to database
   * @param selectedLessonIds Array of lesson IDs to include as context
   * @param customInstruction Optional custom topic instruction
   * @param onProgress Optional callback to track progress: 'text' | 'vocabulary-grammar'
   */
  public static async generateLesson(
    selectedLessonIds: string[] = [],
    customInstruction?: string,
    onProgress?: (stage: 'text' | 'vocabulary-grammar') => void
  ): Promise<FrenchLesson> {
    try {
      console.log('🇫🇷 [FrenchService] Generating new French lesson...');

      // Fetch selected past lessons for context
      let pastLessons: LessonSummary[] = [];
      if (selectedLessonIds.length > 0) {
        console.log('🇫🇷 [FrenchService] Fetching past lessons for context...');
        const allLessons = await this.getPastLessons(100);
        pastLessons = allLessons.lessons.filter(l => selectedLessonIds.includes(l.lesson_id));
      }

      // Step 1: Generate the French text
      if (onProgress) onProgress('text');
      const { lesson_name, lesson_objective, level, text } = await this.generateText(pastLessons, customInstruction);

      // Step 2: Generate vocabulary and grammar from the text
      if (onProgress) onProgress('vocabulary-grammar');
      const { words, grammarPoints } = await this.generateVocabularyAndGrammar(text);

      console.log('🇫🇷 [FrenchService] Lesson generated successfully:', {
        lesson_name,
        lesson_objective,
        level,
        textLength: text.length,
        wordCount: words.length,
        grammarPointsCount: grammarPoints.length
      });

      // Step 3: Save lesson to database
      const savedLesson = await this.saveLesson({
        title: lesson_name,
        objective: lesson_objective,
        level,
        text,
        words,
        grammarPoints
      });

      return savedLesson;
    } catch (error) {
      console.error('🇫🇷 [FrenchService] Error generating lesson:', error);
      
      // Handle authentication errors
      if (error instanceof Error) {
        if (error.message.includes('not authenticated')) {
          throw new Error('Please sign in to generate French lessons.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Save lesson to database
   */
  public static async saveLesson(lessonData: {
    title: string;
    objective: string;
    level: string;
    text: string;
    words: FrenchWord[];
    grammarPoints: GrammarPoint[];
  }): Promise<FrenchLesson> {
    try {
      const result = await apiCall('/lessons', {
        method: 'POST',
        body: JSON.stringify({
          category: 'french',
          lesson_name: lessonData.title,
          lesson_objective: lessonData.objective,
          lesson_content: {
            text: lessonData.text,
            vocabulary: lessonData.words,
            grammar: lessonData.grammarPoints,
            level: lessonData.level
          }
        })
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to save lesson');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Transform database format to component format
      const savedLesson: FrenchLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        title: data.lesson_name,
        text: data.lesson_content.text,
        words: data.lesson_content.vocabulary || [],
        grammarPoints: data.lesson_content.grammar || [],
        level: data.lesson_content.level || 'A2',
        image_url: data.image_url,
        image_prompt: data.image_prompt,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return savedLesson;
    } catch (error) {
      console.error('🇫🇷 [FrenchService] Error saving lesson:', error);
      throw error;
    }
  }

  /**
   * Get past lessons for user (category: french)
   */
  public static async getPastLessons(limit: number = 50, offset: number = 0): Promise<{
    lessons: LessonSummary[];
    total: number;
  }> {
    try {
      const result = await apiCall(`/lessons?category=french&limit=${limit}&offset=${offset}&sortBy=created_at&order=desc`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch lessons');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      return {
        lessons: data.lessons || [],
        total: data.total || 0
      };
    } catch (error) {
      console.error('🇫🇷 [FrenchService] Error fetching past lessons:', error);
      throw error;
    }
  }

  /**
   * Get specific lesson by ID
   */
  public static async getLessonById(lessonId: string): Promise<FrenchLesson> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch lesson');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Transform database format to component format
      const lesson: FrenchLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        title: data.lesson_name,
        text: data.lesson_content.text,
        words: data.lesson_content.vocabulary || [],
        grammarPoints: data.lesson_content.grammar || [],
        level: data.lesson_content.level || 'A2',
        image_url: data.image_url,
        image_prompt: data.image_prompt,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return lesson;
    } catch (error) {
      console.error('🇫🇷 [FrenchService] Error fetching lesson:', error);
      throw error;
    }
  }

  /**
   * Update lesson name
   */
  public static async updateLessonName(lessonId: string, newName: string): Promise<FrenchLesson> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`, {
        method: 'PATCH',
        body: JSON.stringify({ lesson_name: newName })
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to update lesson');
      }

      // API returns data nested under 'data' property
      const data = result.data || result;

      // Transform database format to component format
      const lesson: FrenchLesson = {
        lesson_id: data.lesson_id,
        lesson_name: data.lesson_name,
        lesson_objective: data.lesson_objective,
        category: data.category,
        title: data.lesson_name,
        text: data.lesson_content.text,
        words: data.lesson_content.vocabulary || [],
        grammarPoints: data.lesson_content.grammar || [],
        level: data.lesson_content.level || 'A2',
        image_url: data.image_url,
        image_prompt: data.image_prompt,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      return lesson;
    } catch (error) {
      console.error('🇫🇷 [FrenchService] Error updating lesson:', error);
      throw error;
    }
  }

  /**
   * Delete lesson
   */
  public static async deleteLesson(lessonId: string): Promise<void> {
    try {
      const result = await apiCall(`/lessons/${lessonId}`, {
        method: 'DELETE'
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete lesson');
      }
    } catch (error) {
      console.error('🇫🇷 [FrenchService] Error deleting lesson:', error);
      throw error;
    }
  }

  /**
   * Generate an image for a lesson using AI
   */
  public static async generateLessonImage(
    lessonId: string,
    prompt: string,
    provider?: 'gemini' | 'grok'
  ): Promise<{ image: string; mimeType: string }> {
    try {
      return await generateImageForLesson(lessonId, prompt, provider);
    } catch (error) {
      console.error('🇫🇷 [FrenchService] Error generating image:', error);
      throw error;
    }
  }

  /**
   * Upload image to Firebase Storage and save URL to lesson
   */
  public static async uploadLessonImage(
    lessonId: string,
    imageData: string,
    mimeType: string,
    prompt: string
  ): Promise<string> {
    try {
      // Convert base64 to blob
      const base64Response = await fetch(`data:${mimeType};base64,${imageData}`);
      const blob = await base64Response.blob();

      // Upload to Firebase Storage
      const { storage } = await import('../firebase/config');
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');

      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(7);
      const extension = mimeType.split('/')[1] || 'png';
      const fileName = `${timestamp}_${randomId}.${extension}`;
      const storagePath = `lessons/${lessonId}/images/${fileName}`;

      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, blob, { contentType: mimeType });

      // Get public URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update lesson with image URL and prompt
      await apiCall(`/lessons/${lessonId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          image_url: downloadURL,
          image_prompt: prompt
        })
      });

      return downloadURL;
    } catch (error) {
      console.error('🇫🇷 [FrenchService] Error uploading image:', error);
      throw error;
    }
  }
}

